import { useMemo } from 'react';

import { faDownload, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import i18n from 'config/i18n';
import { isNil } from 'ramda';
import { useTranslation } from 'react-i18next';

import {
  converterForDateTime,
  formatByLocale,
  fromISOString
} from 'utils/date';

import { GeneratedReport, StatusEnum } from 'RestAPIs/reports/generated';

import { IconButton, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { GridColumns } from '@mui/x-data-grid-pro';

import { CustomDataGridAction } from 'components/shared/DataGrid/components/customDataGridAction';

import FontAwesomeSvgIcon from '../../../../../shared/FontAwesomeSvgIcon';
import { loadReport } from './useGetGeneratedReport';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

const PRINT_KEYS = {
  reference_date: 'referenceDate',
  valid_from: 'startDate',
  valid_to: 'endDate',
  session_id: 'sessionId',
  status: 'type'
};

const PRINT_VALUES = {
  reference_date: (date) => formatByLocale(fromISOString(date)),
  valid_from: (date) => formatDateValue(date),
  valid_to: (date) => formatDateValue(date),
  status: (status) => (status === '-10' ? i18n.t('errors') : i18n.t('success'))
};

const isValidDate = (date) => dayjs(date).isValid();

const formatDateValue = (date) => {
  const dateValue = dayjs(date);
  return isValidDate(dateValue) ? dateValue.format('L') : i18n.t('invalidDate');
};

const generateParams = (params, t) =>
  Object.entries(params)
    .filter(([key]) => key in PRINT_KEYS)
    .map(
      ([key, value]) =>
        `${t(PRINT_KEYS[key])}:
         ${(PRINT_VALUES[key] || ((v) => v))(value)}`
    )
    .join(', ');

export const useGeneratedReportsColumnDefinitions = () => {
  const { t } = useTranslation();

  return useMemo<GridColumns<GeneratedReport>>(
    () => [
      {
        field: 'reportName',
        renderHeader: () => (
          <Grid container alignItems="center">
            <Grid item>{t('report')}</Grid>
            <Grid item>
              <Tooltip title={t('generatedReportsRetention')}>
                <IconButton color="primary" size="small">
                  <FontAwesomeSvgIcon icon={faInfoCircle} fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        ),
        type: 'string',
        flex: 1,
        minWidth: 300,
        align: 'left',
        valueFormatter: (item) => t(`generatedReport.${item.value}`)
      },
      {
        field: 'parameters',
        headerName: t('adminPortal.procedures.reports.parameters'),
        flex: 1,
        align: 'left',
        minWidth: 300,
        renderCell: (props) => {
          return <Box>{generateParams(props.value, t)}</Box>;
        }
      },
      {
        field: 'inserted',
        headerName: t('requested'),
        type: 'string',
        flex: 1,
        align: 'left',
        valueFormatter: (item) => {
          return converterForDateTime(item.value);
        }
      },
      {
        field: 'status',
        headerName: t('status'),
        valueFormatter: (item) => {
          return t(`generatedReportStatus.${item.value}`) ?? '';
        },
        type: 'string',
        flex: 1,
        align: 'left'
      },
      {
        field: 'customActions',
        headerName: t('actions'),
        type: 'actions',
        width: 110,
        renderCell: (params) =>
          params.row.status === StatusEnum.DONE ||
          !isNil(params.row.filename) ? (
            <CustomDataGridAction
              key="downloadReport"
              action={{
                icon: faDownload,
                onClick: (id, row) => loadReport(row),
                tooltipTitle: t(
                  'adminPortal.procedures.reports.downloadReport'
                ),
                disabled: true
              }}
              params={params}
            />
          ) : null
      }
    ],
    []
  );
};
